/** One browser entry intent survives refresh; only the server confirms charge and arrival. */
import {S,esc} from '../core/state.js';
import {send,townActionAllowed} from '../core/net.js';
import {huntRegionsFor} from '../world/map.js';
// R166 — 은화를 내는 입장 확인은 브라우저 창이 아니라 도트 확인 창이 묻는다
import {askConfirm} from './dialog.js';
import {fold,loadingOr} from './panels/common.js';
/**
 * 유료 구역 입장 의도 하나 — 검은가시 분지(Q87)와 지옥 분지(UNI-Q185)가 같은 영수증 복구 흐름을 쓴다.
 * 두 벌로 베끼면 한쪽만 고쳐진다: 새로고침 뒤 복구·같은 요청 재확인·다른 캐릭터 재생 차단은 여기 한 곳에 있다.
 */
function paidEntry(key,build){
  const type=build({worldN:0,fee:0}).t,e={pending:null,timer:0,notice:''};
  const keyFor=charId=>`${key}:${charId}`;
  const save=(charId=e.pending?.charId||S.charId)=>{try{if(e.pending)sessionStorage.setItem(keyFor(charId),JSON.stringify(e.pending));else sessionStorage.removeItem(keyFor(charId));return true;}catch{return false;}};
  e.active=()=>e.pending?.charId===S.charId?e.pending:null;
  e.restore=()=>{
    if(e.active())return;
    try{
      const stored=sessionStorage.getItem(keyFor(S.charId));
      const r=JSON.parse(stored||sessionStorage.getItem(key)||'null');
      if(r?.charId===S.charId&&/^[a-f0-9-]{36}$/i.test(r?.request?.receipt)&&r?.request?.t===type){
        e.pending={...r,status:'uncertain'};
        if(!stored&&save())sessionStorage.removeItem(key); // migrate the old one-key intent
      }
    }catch{}
  };
  const transmit=(redraw)=>{const p=e.active();if(!p)return;
    if(!S.connected||S.ws?.readyState!==1){p.status='uncertain';e.notice='연결 복구 뒤 같은 요청을 확인해 달라.';save();redraw(true);return;}
    p.status='waiting';save();
    if(send(p.request)!==true){p.status='uncertain';e.notice='입장 요청을 보내지 못했다. 연결 복구 뒤 같은 요청을 확인해 달라.';save();redraw(true);return;}
    clearTimeout(e.timer);e.timer=setTimeout(()=>{if(e.active()!==p)return;p.status='uncertain';save();redraw(true);},12000);redraw(true);
  };
  e.begin=(d,redraw)=>{e.pending={charId:S.charId,status:'uncertain',request:build(d)};e.notice='';
    if(!save()){e.pending=null;e.notice='입장 요청 번호를 보관하지 못했다. 요금은 보내지 않았다.';redraw(true);return;}
    transmit(redraw);};
  e.retry=(redraw)=>{e.restore();if(e.active()?.status==='waiting')return;transmit(redraw);};
  e.ack=(m)=>{const p=e.active();if(!p||p.request.receipt!==m.receipt)return false;
    clearTimeout(e.timer);e.notice=m.error||(m.status==='committed'?'입장이 확정됐다. 은화는 한 번만 차감됐다.':'입장 저장을 확인 중이다.');
    if(m.status==='committed'||m.status==='rejected'){e.pending=null;save(p.charId);}else{p.status='uncertain';save();}return true;
  };
  /** 결과를 확인하는 동안 새 결제를 시작하지 않는 버튼 줄 */
  e.waiting=(retryAct)=>{const p=e.active();return p?`<div class="ux-note" role="status"><b>입장료 ${Number(p.request.fee).toLocaleString('ko-KR')} 은화 · 결과 확인 중</b><p>입장 결과를 확인 중이다. 새 결제는 시작하지 않는다.</p></div><button onclick="act.${retryAct}()" ${p.status==='waiting'?'disabled':''}>같은 입장 요청 확인</button>`:'';};
  return e;
}
const basin=paidEntry('unige.escape.entry.v1',(d)=>({t:'escapeFieldEnter',receipt:crypto.randomUUID(),worldN:d.worldN,fee:d.fee}));
const hell=paidEntry('unige.hell.entry.v1',(d)=>({t:'hellFieldEnter',receipt:crypto.randomUUID(),worldN:d.worldN,fee:d.fee}));
const fee=(n)=>Number(n).toLocaleString('ko-KR');
/** 권장 레벨 미만이면 확인 창 앞에 실제 레벨과 권장 레벨을 적는다(입장 레벨 제한은 없다) */
function levelWarning(recommended){const level=Number(S.you?.level);return level<recommended?`현재 Lv${level} · 권장 Lv${recommended} 미만이라 매우 위험하다. `:'';}

export function restoreEscapeEntry(){basin.restore();}
export function escapeEntryCard(){
  basin.restore();const d=S.escapeFieldInfo;
  const recommended=Number(d?.recommendedLevel??huntRegionsFor(S.world?.n).escape.level);
  // Q276 — 한 줄 요약 + [자세히](규칙 전문은 펼침 안)
  const info=fold(`<b>권장 Lv${recommended}+</b> 고위험 사냥터 · 귀환 불가 — 서쪽 출구로 나간다`,
    `<p><b>권장 Lv${recommended}+</b> 고위험 사냥터다. 레벨 제한 없이 입장할 수 있지만, 입구부터 Lv${recommended} 몬스터가 등장한다. 입구에는 소수의 몬스터가 있고 안쪽에는 많은 무리와 네임드가 있다. 중앙의 작은 보급지에서는 회복 물약만 살 수 있다. 귀환·순간이동은 막히며 서쪽 출구까지 걸어 나가야 한다. 사망 시에는 기존 손실·부활 규칙을 따른다.</p>`);
  /**
   * R257(DESIGN 14.416) — 사냥터 소켓 위(더보기 → 마을 사람들로 연 역참)에서는 입장을 받지 않는다(1.0 도 공개 마을·텔레포터 앞에서만 —
   * `escape-field.ts` `enterEscapeField`). 전에는 버튼이 켜져 있어 누르면 입장 의도가 「확인 중」에 걸렸다. 안내는 원천 읽기 다리로 받는다.
   */
  // DESIGN 14.599 6단계 — 원천 소켓이거나 게임 서버의 마을 구역(공개 마을·텔레포터 앞)이면 받는다 — 사냥터 소켓만 막는다
  const away=!townActionAllowed('escapeFieldEnter');
  const action=basin.waiting('escapeEntryRetry')
    ||(d?`<p>입장료 <b>${fee(d.fee)} 은화</b> · 가방 부족분은 창고에서 사용</p><button onclick="act.escapeEntry()" ${!d.on||d.inside||away?'disabled':''}>${d.inside?'현재 분지 안':!d.on?'입장 중지':away?'마을·역참 앞에서 입장한다':'검은가시 분지 입장'}</button>`:loadingOr('escapeFieldInfo','입장 안내를 불러오는 중이다.'));
  return `<section class="sec"><h3>검은가시 분지</h3><div class="ux-facts"><span class="ux-fact">권장 <b>Lv${recommended}+</b></span><span class="ux-fact">탈출 <b>서쪽 출구까지 걷기</b></span><span class="ux-fact">귀환·순간이동 <b>사용 불가</b></span></div>${action}${info}<button class="ghost" onclick="act.escapeEntryInfo()">안내 새로고침</button><p role="status">${esc(basin.notice)}</p></section>`;
}
export function submitEscapeEntry(redraw){
  basin.restore();if(basin.active())return;const d=S.escapeFieldInfo;
  if(!d?.on||d.inside||!townActionAllowed('escapeFieldEnter'))return;
  // 창이 떠 있는 동안 다른 창·새로고침 복구로 입장이 시작됐거나 입장이 멈췄을 수 있다 — 고른 순간 다시 본다. 낸다고 한 요금(d)으로 보낸다
  askConfirm(`${levelWarning(Number(d.recommendedLevel??huntRegionsFor(S.world?.n).escape.level))}${fee(d.fee)} 은화를 내고 검은가시 분지에 입장할까? 귀환·순간이동 없이 서쪽 출구까지 걸어서 탈출해야 한다.`,()=>{
    basin.restore();if(basin.active())return;const now=S.escapeFieldInfo;if(!now?.on||now.inside)return;
    basin.begin(d,redraw);
  },{ok:'입장한다'});
}
export function retryEscapeEntry(redraw){basin.retry(redraw);}
export function escapeEntryAck(m){return basin.ack(m);}

/**
 * UNI-Q185 지옥 분지 — 분지 보급소의 지옥문지기(입장)와 지옥 안쪽 보급소의 지옥문(퇴장)이 같은 창을 쓴다.
 * `where` 는 화면이 지금 좌표로 잰 자리(`basinCampAt`·`hellCampAt`·`hellFieldAt` — 서버와 같은 함수)다.
 * 서버도 같은 보급소 판정으로 받는다. 권장 레벨·입장료·몬스터 특성은 서버가 준 값(`hellFieldInfo`)을 적는다 — 콘솔에서 바꾸면 이 문장도 바뀐다.
 */
let leaveNotice='',leaveBusy=false,leaveTimer=0;
export function restoreHellEntry(){hell.restore();}
export function hellEntryCard(where){
  hell.restore();const d=S.hellFieldInfo;
  if(where.inside){
    const button=where.atExit?`<button onclick="act.hellLeave()" ${leaveBusy?'disabled':''}>${leaveBusy?'지옥문을 지나는 중':'검은가시 분지 보급소로 나가기'}</button>`
      :'<button disabled>안쪽 보급소 안에서만 나갈 수 있다</button>';
    return `<section class="sec"><h3>지옥문</h3><p>문을 지나면 검은가시 분지 보급소로 돌아간다. 요금은 없다. 지옥 분지에서 나가는 길은 이 문뿐이다 — 귀환 주문서·순간이동은 듣지 않는다.</p>${button}<p role="status">${esc(leaveNotice)}</p></section>`;
  }
  const recommended=Number(d?.recommendedLevel??0);
  const traits=d?`몬스터는 사람이 주는 피해를 ${d.cutPct}% 덜 받고, 맞으면 ${d.stunPct}% 확률로 ${d.stunTenths/10}초 동안 몸이 굳는다.`:'';
  /** UNI-Q204 — 「무리 지어 있다」는 전에는 사실이 아니었다(낱마리로 흩어져 돌았다). 지금은 사실이고, 수는 서버 값에서 적는다 */
  const range=(lo,hi)=>lo===hi?`${lo}`:`${lo}~${hi}`;
  const packs=d?.packsOn?` 네임드는 일반 몬스터 ${range(d.packMin,d.packMax)}마리를 이끌고 보스 곁에는 호위 ${range(d.escortMin,d.escortMax)}마리가 붙는다 — <b>한 마리를 건드리면 그 무리가 함께 온다.</b>`:'';
  // Q276 — 한 줄 요약 + [자세히](규칙 전문은 펼침 안)
  const info=fold(`${recommended?`<b>권장 Lv${recommended}+</b> · `:''}분지 아래 지옥 · 나오는 길은 지옥문뿐`,
    `<p>${recommended?`<b>권장 Lv${recommended}+</b> — `:''}검은가시 분지 아래의 지옥이다. 레벨 제한 없이 내려갈 수 있지만 ${d?.monsterLevel?`Lv${d.monsterLevel} 이상 `:''}몬스터가 무리 지어 있다.${packs} ${traits} 분지보다 경험치와 은화 잡템이 훨씬 많고, 네임드·보스는 날개를, 보스는 궁극 장비까지 떨어뜨린다. 안에서는 귀환·순간이동이 안 되고, 나오는 길은 안쪽 보급소의 지옥문뿐이다. 안쪽 보급소에서는 회복 물약만 산다. 사망 시에는 기존 손실·부활 규칙을 따른다.</p>`);
  const action=hell.waiting('hellEntryRetry')
    ||(d?`<p>입장료 <b>${fee(d.fee)} 은화</b> · 가방 부족분은 창고에서 사용</p><button onclick="act.hellEntry()" ${!d.on||!where.atGate?'disabled':''}>${!d.on?'입장 중지':!where.atGate?'분지 보급소 안에서만 내려갈 수 있다':'지옥 분지로 내려가기'}</button>`:loadingOr('hellFieldInfo','입장 안내를 불러오는 중이다.'));
  return `<section class="sec"><h3>지옥 분지</h3><div class="ux-facts">${recommended?`<span class="ux-fact">권장 <b>Lv${recommended}+</b></span>`:''}<span class="ux-fact">탈출 <b>안쪽 보급소의 지옥문</b></span><span class="ux-fact">귀환·순간이동 <b>사용 불가</b></span></div>${traits?`<p class="qt">${traits}</p>`:''}${action}${info}<button class="ghost" onclick="act.hellEntryInfo()">안내 새로고침</button><p role="status">${esc(hell.notice)}</p></section>`;
}
export function submitHellEntry(redraw,where){
  hell.restore();if(hell.active())return;const d=S.hellFieldInfo;
  if(!d?.on||!where.atGate)return;
  askConfirm(`${levelWarning(Number(d.recommendedLevel??0))}${fee(d.fee)} 은화를 내고 지옥 분지로 내려갈까? 귀환·순간이동이 막히고, 나오는 길은 안쪽 보급소의 지옥문뿐이다.`,()=>{
    hell.restore();if(hell.active())return;if(!S.hellFieldInfo?.on)return;
    hell.begin(d,redraw);
  },{ok:'내려간다'});
}
export function retryHellEntry(redraw){hell.retry(redraw);}
/** 지옥문 퇴장은 무료다 — 원자 귀환 저장의 결과를 기다리는 동안 버튼을 잠근다 */
export function leaveHell(redraw){
  if(leaveBusy)return;
  if(!S.connected||S.ws?.readyState!==1){leaveNotice='연결 복구 뒤 다시 눌러 달라.';redraw(true);return;}
  leaveBusy=true;leaveNotice='';send({t:'hellFieldLeave'});clearTimeout(leaveTimer);
  leaveTimer=setTimeout(()=>{if(!leaveBusy)return;leaveBusy=false;leaveNotice='응답이 없다. 자리를 확인하고 다시 눌러 달라.';redraw(true);},12000);redraw(true);
}
/** `hellFieldAck` — 서버가 퇴장 결과에 `leave` 를 붙인다. 없으면 입장 영수증 결과. 처리했으면 true */
export function hellFieldAck(m){
  if(!m?.leave)return hell.ack(m);
  clearTimeout(leaveTimer);leaveBusy=m.status==='pending';leaveNotice=m.status==='left'?'':String(m.error||'나가지 못했다.');return true;
}
